// Entity payloads are intentionally untyped on the frontend (treated as
// `Record<string, unknown>` by the generic browser) - the per-field metadata
// in `EntitySpecs.tsx` is the single source of truth.

export interface BaseEntity {
  id: number;
}

// Field metadata used by the generic editor / details view
export interface FieldDef {
  name: string;
  label: string;
  type: 'string' | 'multiline' | 'number' | 'boolean' | 'fk' | 'logo' | 'picture' | 'datetime' | 'enum' | 'range' | 'document' | 'documents';
  fkPath?: string; // for type='fk', the entity URL slug for the FK dropdown
  // For type='enum': the (value, label) options shown in the dropdown.
  options?: { value: string; label: string }[];
  // For type='range': numeric (from, to) pair plus an optional free-text
  // comment, all rendered on a single row. ``name`` on the range field is
  // synthetic (not sent to the API) - these three sub-keys are the actual
  // payload columns.
  range?: { from: string; to: string; comment?: string };
  required?: boolean;
  decimals?: number; // for type='number': max decimal digits to display
  group?: string; // optional sub-group label in the detail view
  // When set, the editor renders this field side-by-side with the next
  // field in the spec (sharing one row). Useful for value+unit pairs like
  // ``# Employees`` and the year of that headcount.
  inlineWithNext?: boolean;
  // Server-managed fields (e.g. audit columns) - displayed but never editable
  // and never sent in create/update payloads.
  readOnly?: boolean;
}

// Describes a related set of items shown as a tab in the detail page.
// - Direct relation: items in `targetPath` whose `fkField` equals the current item's id.
// - Join-table relation: rows of `joinPath` where `joinSelfField` equals the current
//   item's id; their `joinOtherField` then points to entries of `otherPath`.
// - Managed M-N relation: hides the join table behind a dedicated endpoint.
//   GET  managedPath(parentId) returns the list of linked peer entities.
//   PUT  managedPath(parentId) with body `number[]` (peer ids) replaces the set.
export interface RelationDef {
  key: string;
  label: string;
  targetPath?: string;
  fkField?: string;
  joinPath?: string;
  joinSelfField?: string;
  joinOtherField?: string;
  otherPath?: string;
  managedPath?: (parentId: number) => string;
}

export interface EntitySpec {
  key: string; // unique key used in URL (matches backend slug)
  label: string; // human readable plural
  singular: string;
  path: string; // backend path slug, e.g. "suppliers"
  // Identifier sent to the backend ``/documents`` endpoints as ``entity_type``.
  // Required on entities that declare ``document`` / ``documents`` fields
  // (the value must match the backend's ``_ENTITY_REGISTRY`` keys, e.g.
  // "supplier" or "cell_product").
  entityType?: string;
  searchByCountry?: boolean; // expose the dedicated "Search by country" field
  fields: FieldDef[];
  relations?: RelationDef[];
}

// User-facing category dropdown (matches the legacy UI).
export interface Category {
  value: string; // either an entity key or 'all'
  label: string;
}

export const CATEGORIES: Category[] = [
  { value: 'suppliers', label: 'Suppliers' },
  { value: 'cell-types', label: 'Cell Type' },
  { value: 'technology-types', label: 'Technology' },
    { value: 'technologies', label: 'Subtechnology' },
  { value: 'anodes', label: 'Anode' },
  { value: 'cathodes', label: 'Cathode' },
  { value: 'electrolytes', label: 'Electrolyte' },
  { value: 'markets', label: 'Markets' },
];

export interface HealthStatus {
  status: string;
  timestamp: string;
  checks: {
    api: string;
    database: string;
  };
  test_data?: {
    id: number;
    test_value: string;
    created_at: string | null;
  };
  error?: string;
}
